import React, { useState, useEffect, useCallback } from 'react';
import { getReports, getReportById, processReport, getReportCategoryLabel, getReportStatusLabel, getReportStatusClass } from '../services/reportService';
import { getAdminData } from '../services/authService';
import { formatDate } from '../utils/dateFormat';
import { IMAGES } from '../constants/images';
import Header from '../components/common/Header';
import PageHeader from '../components/common/PageHeader';
import Snackbar from '../components/common/Snackbar';
import useSnackbar from '../hooks/useSnackbar';
import ReportDetailModal from '../components/common/ReportDetailModal';
import '../css/ReportManagement.css';

const ReportManagement = () => {
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    page: 0,
    size: 20,
  });
  const [adminInfo, setAdminInfo] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getReports(filters);
      setReports(result.reports || []);
      setTotal(result.total || 0);
    } catch (error) {
      showSnackbar(error.message || '신고 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showSnackbar]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    setAdminInfo(getAdminData());
  }, []);

  const handleStatusFilter = (status) => {
    setFilters((prev) => ({ ...prev, status: status === prev.status ? '' : status, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleSearch = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value, page: 0 }));
  }, []);

  const totalPages = Math.ceil(total / filters.size);

  const openDetailModal = async (report) => {
    setDetailLoading(true);
    setDetailModalOpen(true);
    try {
      const detail = await getReportById(report.report_id);
      setSelectedReport(detail);
    } catch (error) {
      showSnackbar(error.message || '신고 상세 정보를 불러오는데 실패했습니다.', 'error');
      setDetailModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleProcessReport = async (newStatus, sanctionType = null, sanctionReason = '') => {
    if (!selectedReport) return;

    try {
      await processReport(selectedReport.report_id, {
        newStatus,
        sanctionType,
        sanctionReason
      });
      showSnackbar('신고가 처리되었습니다.', 'success');
      setDetailModalOpen(false);
      setSelectedReport(null);
      fetchReports();
    } catch (error) {
      showSnackbar(error.message || '신고 처리에 실패했습니다.', 'error');
    }
  };

  const statusFilters = [
    { value: '', label: '전체' },
    { value: 'PENDING', label: '대기중' },
    { value: 'REVIEWED', label: '검토됨' },
    { value: 'RESOLVED', label: '처리완료' },
    { value: 'REJECTED', label: '반려됨' }
  ];

  return (
    <div className="report-management">
      <Header />

      <PageHeader title="신고 관리" />
      <div className="report-page">
        <div className="report-content">
          <div className="report-filters">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                className={`filter-btn ${filters.status === filter.value ? 'active' : ''}`}
                onClick={() => handleStatusFilter(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="report-search-bar">
            <div className="report-search-group">
              <div className="report-search-input">
                <img src={IMAGES.GLASSES} alt="" className="search-icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="고객 이름, 매장 이름으로 검색"
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-label="신고 검색"
                />
              </div>
              <span className="total-count" aria-live="polite" aria-atomic="true">총 {total}개</span>
            </div>
          </div>

          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>고객 이름</th>
                  <th>매장 이름</th>
                  <th>신고 유형</th>
                  <th>신고 사유</th>
                  <th>상태</th>
                  <th>신고일시</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="loading-cell">
                      로딩 중...
                    </td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-cell">
                      등록된 신고가 없습니다.
                    </td>
                  </tr>
                ) : (
                  reports.map((report) => (
                    <tr
                      key={report.report_id}
                      onClick={() => openDetailModal(report)}
                      style={{ cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openDetailModal(report);
                        }
                      }}
                      aria-label={`${report.customer_name} 신고 상세 정보 보기`}
                    >
                      <td>{report.customer_name || '-'}</td>
                      <td>{report.store_name || '-'}</td>
                      <td>{getReportCategoryLabel(report.category)}</td>
                      <td className="reason-cell">{report.reason || '-'}</td>
                      <td>
                        <span className={`status-badge ${getReportStatusClass(report.status)}`}>
                          {getReportStatusLabel(report.status)}
                        </span>
                      </td>
                      <td>{formatDate(report.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <nav className="pagination" aria-label="페이지 네비게이션">
              <button
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={filters.page === 0}
                className="page-btn"
                aria-label="이전 페이지"
              >
                이전
              </button>
              <span className="page-info" aria-current="page">
                {filters.page + 1} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page >= totalPages - 1}
                className="page-btn"
                aria-label="다음 페이지"
              >
                다음
              </button>
            </nav>
          )}
        </div>
      </div>

      <Snackbar
        message={snackbar.message}
        type={snackbar.type}
        isOpen={snackbar.isOpen}
        onClose={hideSnackbar}
        duration={3000}
      />

      <ReportDetailModal
        isOpen={detailModalOpen}
        loading={detailLoading}
        report={selectedReport}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedReport(null);
        }}
        onProcess={handleProcessReport}
      />
    </div>
  );
};

export default ReportManagement;